import { Roles } from '@consts/role';
import { PageLabels } from '@consts/webPage';
import { User } from '@models/auth';
import { Membership } from '@models/membership';
import { Product } from '@models/product';
import { ProductOrderViewModel } from '@models/productOrderView';
import { SalesChannelsType } from '@models/sales-channels';
import { isValidDateString } from './date';
import { resolveMembershipDiscount } from './membership-discount';

export enum MessageActions {
  UPDATE_TOKEN = 'update_token',
  // MOVE_FROM_NOTIFICATION = 'move_from_notification',
  MOVE_FROM_NOTIFICATION = 'notification',
  BACK_TO_PREVIOUS = 'back_to_previous',
  SHARE_URL = 'share_url',
  GET_DEVICEID = 'get_deviceId',
  CLOSE_MODAL = 'close_modal',
  GET_ASYNC_STORAGE_LOGIN_USERINFO = 'get_async_storage_login_userinfo',
  DONE_LEGACY_LOGIN = 'done_legacy_login',
  LOGOUT = 'logout',
  LOGIN = 'login',
  DENY_PARENT_ETC_PUSH = 'deny_parent_etc_push',
  ACCPET_PARENT_ETC_PUSH = 'accept_parent_etc_push',
  DENY_TUTOR_ETC_PUSH = 'deny_tutor_etc_push',
  ACCPET_TUTOR_ETC_PUSH = 'accept_tutor_etc_push',
  DENY_ETC_PUSH = 'deny_etc_push',
  POP_STACK = 'pop_stack',
  POP_STACK_SWIPE_GESTURE = 'pop_stack_swipe_gesture',
  PUSH_STACK = 'push_stack',
  GET_USER_INFO = 'get_user_info',
  MOVE_FROM_DEEPLINK = 'move_from_deeplink',
  DOWNLOAD_IMAGES = 'download_images',
  SEND_INAPP_BROWSER = 'send_inapp_browser',
}
export interface NativeMessage {
  type: MessageActions;
  data: object;
}

export const scrollToTop = () => {
  document.body.scrollTo(0, 0);
};

export const isPartnerRole = (user?: User | null) => {
  if (
    user?.roles?.includes(Roles.SELLER) ||
    user?.roles?.includes(Roles.TUTOR) ||
    user?.roles?.includes(Roles.EDITOR) ||
    user?.roles?.includes(Roles.PARTNER) ||
    user?.roles?.includes(Roles.ADMIN) ||
    user?.roles?.includes(Roles.MEMBERSHIP_PARTNER) ||
    user?.roles?.includes(Roles.INSTRUCTOR)
  ) {
    return true;
  }

  if (user?.role === Roles.SELLER) {
    return true;
  }

  return false;
};

export const isNoneSellerRole = (user: User) => {
  if (
    user.roles?.includes(Roles.SELLER) ||
    user.roles?.includes(Roles.TUTOR) ||
    user.roles?.includes(Roles.PARTNER) ||
    user.roles?.includes(Roles.ADMIN)
  ) {
    return false;
  }

  if (user.role === Roles.SELLER || user.role === Roles.TUTOR) {
    return false;
  }

  return true;
};

export const getLocaleText = (location: { state: string; city: string }) => {
  const stateText = location?.state?.replace('특별시', '').replace('광역시', '').replace(/도$/, '');
  const cityText =
    location?.city?.length > 2
      ? location?.city.replace(/시$/, '').replace(/군$/, '').replace(/구$/, '')
      : location?.city;
  return `${stateText} ${cityText}`;
};

export const getNativeAppInfoFromUserAgent = (): {
  isNativeApp: boolean;
  appVersion: string | undefined;
  appPlatform: 'ios' | 'android' | undefined;
} => {
  const isNativeApp = /igogo-react-native-app/i.test(navigator.userAgent);
  let appPlatform: 'ios' | 'android' | undefined = undefined;
  let appVersion: string | undefined = undefined;
  if (isNativeApp) {
    if (navigator.userAgent.endsWith('ios')) {
      appPlatform = 'ios';
    } else if (navigator.userAgent.endsWith('android')) {
      appPlatform = 'android';
    }
    const regex = /igogo-react-native-app\/(\S+)/;
    const match = navigator.userAgent.match(regex);
    if (match) {
      appVersion = match[1];
    }
  }
  return {
    isNativeApp,
    appVersion,
    appPlatform,
  };
};

export const compareVersion = (currentVersion: string | undefined, targetVersion: string): boolean => {
  if (!currentVersion) {
    return true;
  }
  const parseVersion = (version: string) => {
    const [major = 0, minor = 0, patch = 0] = version.split('.').map((item) => Number(item));
    return { major, minor, patch };
  };
  const current = parseVersion(currentVersion);
  const target = parseVersion(targetVersion);
  if (target.major !== current.major) {
    return target.major - current.major > 0;
  }
  if (target.minor !== current.minor) {
    return target.minor - current.minor > 0;
  }
  return target.patch - current.patch > 0;
};

export const sendMessageToNative = (message: NativeMessage) => {
  return (window as any).ReactNativeWebView?.postMessage(JSON.stringify(message));
};

export const downloadImageFilesOnNativeApp = (images: string[]) => {
  const response = {
    type: MessageActions.DOWNLOAD_IMAGES,
    data: {
      images,
    },
  };
  sendMessageToNative(response);
};

export const openLink = (url: string) => {
  const { isNativeApp, appVersion } = getNativeAppInfoFromUserAgent();
  const hasParameter = !!url?.split('?')?.[1];
  let canOpenInAppBrowser = isNativeApp && appVersion && !url?.startsWith('tel');
  if (hasParameter) {
    const isUpdateRequired = compareVersion(appVersion, '2.2.9');
    canOpenInAppBrowser = isNativeApp && appVersion && !url?.startsWith('tel') && !isUpdateRequired;
  }

  if (canOpenInAppBrowser) {
    const postMessage = {
      type: MessageActions.SEND_INAPP_BROWSER,
      data: {
        url: url,
      },
    };
    sendMessageToNative(postMessage);
  } else {
    window.open(url, '_blank');
  }
};

export const discountedAmountBy = (amount: number, discountPercent: number) => {
  const result = ((amount || 0) * (100 - (discountPercent || 0))) / 100;
  return Math.floor(result / 10) * 10;
};

export const getAppliedMembership = (product?: Product, userMembership?: Partial<Membership>) => {
  const productMemberships: Partial<Membership>[] = product?.memberships || [];
  const productId = product?._id;
  const appliedMembership = productMemberships.find((membership) => {
    return membership.alias === userMembership?.alias && userMembership?.qualified;
  });

  if (!appliedMembership) {
    return appliedMembership;
  }

  const resolution = resolveMembershipDiscount(appliedMembership, productId);
  return {
    ...appliedMembership,
    discountPercent: resolution.percent,
  };
};

export const getChannelText = (channel?: SalesChannelsType) => {
  let channelText = '앱';
  if (channel) {
    if ([SalesChannelsType.NAVER_STORE, SalesChannelsType.INBOUND_NAVER].includes(channel)) {
      channelText = '네이버';
    } else if ([SalesChannelsType.KAKAO_RESERVATION].includes(channel)) {
      channelText = '카카오';
    } else if ([SalesChannelsType.BENEPIA].includes(channel)) {
      channelText = '베네피아';
    } else if ([SalesChannelsType.EZWEL].includes(channel)) {
      channelText = '이지웰';
    }
  }
  return channelText;
};

export const EZWEL_PARTNER_REFUND_GUIDE = {
  title: '이지웰 환불 안내',
  content:
    '이지웰 결제건은 파트너센터에서 환불할 수 없습니다.\n고객에게 환불될 예정임을 안내해주신 후, 파트너님께서 해당 고객 환불건을 아이고고 파트너스에 접수해주세요. (고객이름, 전화번호, 환불여부 및 비고사항)',
} as const;

export const isPassedDate = (date: Date) => {
  const today = new Date();
  if (date.getTime() < today.getTime()) {
    return true;
  }
  return false;
};

export const isSellerOrPartner = (user?: User | null) => {
  if (user?.roles?.includes(Roles.SELLER) || user?.roles?.includes(Roles.PARTNER)) {
    return true;
  }
  return false;
};

export const getPaymentAmountWithMembershipDiscountText = (payment?: ProductOrderViewModel['payment']) => {
  const paymentAmount = payment?.amount || 0;
  const membershipDiscountAmount = payment?.membershipDiscountAmount || 0;
  const couponDiscountAmount = payment?.couponDiscountAmount || 0;
  return `${paymentAmount.toLocaleString()}원 ${membershipDiscountAmount ? `(판매가 ${(paymentAmount + membershipDiscountAmount).toLocaleString()}원 + 멤버십할인 -${membershipDiscountAmount.toLocaleString()}원)` : ''} ${couponDiscountAmount ? `(쿠폰할인 ${couponDiscountAmount.toLocaleString()}원)` : ''}`;
};

export const getPageLabel = (path: string) => {
  if (PageLabels[path]) {
    return PageLabels[path];
  }

  const pathSegments = path.split('/').filter(Boolean);
  const labelKeys = Object.keys(PageLabels);

  for (const key of labelKeys) {
    const keySegments = key.split('/').filter(Boolean);
    if (pathSegments.length === keySegments.length) {
      const match = keySegments.every((segment, i) => {
        return segment.startsWith(':') || segment === pathSegments[i];
      });
      if (match) {
        return PageLabels[key];
      }
    }
  }

  return '알 수 없음';
};

// 학년 컨텍스트 확인 함수
const hasGradeContext = (str: string): boolean => {
  const context = str.toLowerCase();

  // 학년 패턴들
  const gradePatterns = [/\d+[-~]\d+\s*학년/, /\(\d+[-~]\d+학년\)/, /\(\d+[-~]\d+\s*학년\)/];

  for (const pattern of gradePatterns) {
    if (pattern.test(str)) {
      return true;
    }
  }

  // MMDDNoYearDash 패턴의 경우 추가 컨텍스트 검증
  if (context.includes('학년') || context.includes('세') || context.includes('살') || context.includes('개월')) {
    return true;
  }

  return false;
};

const convertToFourDigitYear = (year: number) => {
  if (year > 999) return year; // 이미 4자리면 그대로 반환

  const currentYear = new Date().getFullYear();
  const century = Math.floor(currentYear / 100) * 100;
  const fullYear = century + year;

  // 예를 들어 90~99는 1900년대, 00~89는 2000년대로 변환
  return fullYear > currentYear + 10 ? fullYear - 100 : fullYear;
};

const formatDateToYYYYMMDD = (dateString: string, format: string): string | null => {
  try {
    const currentYear = new Date().getFullYear();

    switch (format) {
      case 'KoreanFull': {
        const [year, month, day] = dateString.match(/\d+/g) || [];
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      case 'KoreanShortYear': {
        const [yearShort, month, day] = dateString.match(/\d+/g) || [];
        const fullYear = convertToFourDigitYear(Number(yearShort));
        return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      case 'KoreanShort': {
        const [monthKor, dayKor] = dateString.match(/\d+/g) || [];
        return `${currentYear}-${monthKor?.padStart(2, '0')}-${dayKor?.padStart(2, '0')}`;
      }

      case 'YYYYMMDDDash': {
        const [yearDash, monthDash, dayDash] = dateString.split('-');
        return `${convertToFourDigitYear(Number(yearDash))}-${monthDash.padStart(2, '0')}-${dayDash.padStart(2, '0')}`;
      }

      case 'YYYYMMDDDot':
        return dateString.replace(/\./g, '-');

      case 'YYYYMMDDSlash':
        return dateString.replace(/\//g, '-');

      case 'MMDDNoYearDash': {
        const [monthNoYearDash, dayNoYearDash] = dateString.split('-');
        return `${currentYear}-${monthNoYearDash.padStart(2, '0')}-${dayNoYearDash.padStart(2, '0')}`;
      }

      case 'MMDDNoYearDot': {
        const [monthNoYearDot, dayNoYearDot] = dateString.split('.');
        return `${currentYear}-${monthNoYearDot.padStart(2, '0')}-${dayNoYearDot.padStart(2, '0')}`;
      }

      case 'MMDDNoYearSlash': {
        const [monthNoYearSlash, dayNoYearSlash] = dateString.split('/');
        return `${currentYear}-${monthNoYearSlash.padStart(2, '0')}-${dayNoYearSlash.padStart(2, '0')}`;
      }

      default:
        return null;
    }
  } catch (error) {
    return null;
  }
};

export type DateFormat =
  | 'KoreanFull'
  | 'KoreanShort'
  | 'KoreanShortYear'
  | 'YYYYMMDDDash'
  | 'YYYYMMDDDot'
  | 'YYYYMMDDSlash'
  | 'MMDDNoYearDash'
  | 'MMDDNoYearDot'
  | 'MMDDNoYearSlash';

export const parseDatesAndTimes = (
  array: string[] = [],
): {
  matchDays: string[];
  formattedMatchDays: string[];
  formattedMatchDaysWithTime: string[];
  matchTimes: string[];
  matches: string[];
  notMatches: string[];
} => {
  const datePatterns = {
    KoreanFull: /\d{4}년\s*\d{1,2}월\s*\d{1,2}일/g,
    KoreanShortYear: /\d{2}년\s*\d{1,2}월\s*\d{1,2}일/g,
    KoreanShort: /\d{1,2}월\s*\d{1,2}일/g,
    YYYYMMDDDash: /\d{1,4}-\d{1,2}-\d{1,2}/g,
    YYYYMMDDDot: /\d{4}\.\d{1,2}\.\d{1,2}/g,
    YYYYMMDDSlash: /\d{1,4}\/\d{1,2}\/\d{1,2}/g,
    MMDDNoYearDash: /\d{1,2}-\d{1,2}/g,
    MMDDNoYearSlash: /\d{1,2}\/\d{1,2}/g,
    MMDDNoYearDot: /\d{1,2}\.\d{1,2}/g,
  };

  const timePatterns = {
    HHMM: /\d{1,2}:\d{1,2}/,
    KoreanHHMM: /\d{1,2}시\d{1,2}/,
    KoreanHH: /\d{1,2}시/,
  };

  // 휴대폰 번호 패턴 (010-xxxx-xxxx, 010xxxxxxxx)
  const phonePatterns = [/010-\d{4}-\d{4}/, /010\d{8}/];

  const result = {
    matchDays: [] as string[],
    formattedMatchDays: [] as string[],
    formattedMatchDaysWithTime: [] as string[],
    matchTimes: [] as string[],
    matches: [] as string[],
    notMatches: [] as string[],
  };

  array.forEach((dateTimeString: string) => {
    // 1. 휴대폰 번호 예외 처리
    for (const phonePattern of phonePatterns) {
      if (phonePattern.test(dateTimeString.replace(/\s+/g, ''))) {
        result.notMatches.push(dateTimeString);
        return;
      }
    }

    let matched = false;
    let formattedTime: string | null = null;

    // Check for times
    for (const pattern of Object.values(timePatterns)) {
      const timeMatch = dateTimeString.match(pattern);
      if (timeMatch) {
        matched = true;
        result.matchTimes.push(timeMatch[0]);
        formattedTime = timeMatch[0];
        break;
      }
    }

    // Check for dates
    for (const [format, pattern] of Object.entries(datePatterns)) {
      const matches = dateTimeString.match(pattern);
      if (matches) {
        for (let i = 0; i < matches.length; i++) {
          const date = matches[i];

          // 학년 컨텍스트가 있는 경우 날짜 파싱 건너뛰기
          if (hasGradeContext(dateTimeString)) {
            continue;
          }

          const formattedDate = formatDateToYYYYMMDD(date, format as DateFormat);
          if (formattedDate && isValidDateString(formattedDate)) {
            matched = true;
            result.matchDays.push(date);
            result.formattedMatchDays.push(formattedDate);
            result.formattedMatchDaysWithTime.push(`${formattedDate} ${formattedTime || ''}`.trim());
          }
        }
        break;
      }
    }

    if (matched) {
      result.matches.push(dateTimeString);
    } else {
      result.notMatches.push(dateTimeString);
    }
  });

  return result;
};
